import React, { useState, useEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Ticket, Users, TrendingUp, Settings, Shield, AlertCircle, CheckCircle, Clock, Activity, LogOut, Bell, Search, ChevronRight, Database, Smartphone } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import adminService from '../../services/adminService';
import { supportTicketService } from '../../services/supportTicketService';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { LoadingState } from '../../components/ui/LoadingStates';
import Icon from '../../components/AppIcon';



export default function AdminOperationsDashboard() {
  const navigate = useNavigate();
  const authContext = useAuth();
  const { user = null, signOut = async () => {} } = authContext || {};
  const [adminProfile, setAdminProfile] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [seoStats, setSeoStats] = useState(null);

  const quickActions = [
    {
      title: 'User Management',
      description: 'Manage user accounts and permissions',
      icon: Users,
      color: 'blue',
      path: '/admin-user-management-system',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'Support Tickets',
      description: 'View and manage support requests',
      icon: Ticket,
      color: 'purple',
      path: '/admin-support-management-system',
      permission: ['super_admin', 'admin', 'moderator', 'support']
    },
    {
      title: 'Content Moderation',
      description: 'Review flagged content and jobs',
      icon: Shield,
      color: 'orange',
      path: '/admin-content-moderation-dashboard',
      permission: ['super_admin', 'admin', 'moderator']
    },
    {
      title: 'Analytics Dashboard',
      description: 'View platform analytics and insights',
      icon: TrendingUp,
      color: 'green',
      path: '/analytics-dashboard',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'Error Monitoring',
      description: 'Track and resolve system errors',
      icon: AlertCircle,
      color: 'red',
      path: '/error-monitoring-dashboard',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'Security Monitoring',
      description: 'Real-time security monitoring and alerts',
      icon: Shield,
      color: 'red',
      path: '/security-monitoring-dashboard',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'Security Audit',
      description: 'Comprehensive security testing and vulnerability scanning',
      icon: Shield,
      color: 'purple',
      path: '/security-audit-dashboard',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'Backup & Recovery',
      description: 'Database backup and disaster recovery',
      icon: Database,
      color: 'green',
      path: '/backup-recovery-dashboard',
      permission: ['super_admin']
    },
    {
      title: 'SEO Validation',
      description: 'Monitor Google Jobs compliance',
      icon: Search,
      color: 'teal',
      path: '/seo-validation-dashboard',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'AI System Testing',
      description: 'Test AI failover and performance',
      icon: Activity,
      color: 'pink',
      path: '/ai-system-testing-dashboard',
      permission: ['super_admin', 'admin']
    },
    {
      title: 'Mobile Responsiveness',
      description: 'Test mobile layout and touch interactions',
      icon: Smartphone,
      color: 'pink',
      path: '/mobile-responsiveness-dashboard',
      permission: ['super_admin', 'admin']
    }
  ];

  // Filter quick actions based on admin role
  const filteredQuickActions = quickActions?.filter(action => {
    if (!adminProfile?.role) return false;
    return action?.permission?.includes(adminProfile?.role);
  });

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) {
      navigate('/admin-authentication-portal');
      return;
    }

    try {
      setLoading(true);

      // Load admin profile
      const profile = await adminService?.getAdminProfile(user?.id);
      setAdminProfile(profile);

      // Load real-time metrics
      const metricsData = await adminService?.getRealTimeMetrics();
      setMetrics(metricsData);

      // Load recent tickets
      const tickets = await supportTicketService?.getAllTickets({ limit: 10 });
      setRecentTickets(tickets?.slice(0, 5));

      // Load recent activity
      const activity = await adminService?.getActivityLog({ limit: 10 });
      setRecentActivity(activity);

      // Load SEO validation stats (aggregate across all companies)
      try {
        // For admin, we'll show a simplified version
        // In production, you'd aggregate stats across all companies
        setSeoStats({
          totalJobsIndexed: metricsData?.totalJobs || 0,
          averageComplianceScore: 85, // Mock data - would calculate from all companies
          jobsNeedingAttention: Math.round((metricsData?.totalJobs || 0) * 0.15)
        });
      } catch (err) {
        console.error('Error loading SEO stats:', err);
      }

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Log logout activity
      if (adminProfile) {
        await adminService?.logActivity(
          'logout',
          'Admin logout',
          user?.id,
          null,
          { adminRole: adminProfile?.role }
        );
      }

      await signOut();
      navigate('/admin-authentication-portal');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800 border-purple-300',
      admin: 'bg-blue-100 text-blue-800 border-blue-300',
      moderator: 'bg-green-100 text-green-800 border-green-300',
      support: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors?.[role] || colors?.support;
  };

  const getTicketStatusIcon = (status) => {
    const icons = {
      new: AlertCircle,
      assigned: Users,
      in_progress: Clock,
      resolved: CheckCircle,
      closed: CheckCircle
    };
    return icons?.[status] || AlertCircle;
  };

  const getTicketStatusColor = (status) => {
    const colors = {
      new: 'text-blue-600 bg-blue-100',
      assigned: 'text-purple-600 bg-purple-100',
      in_progress: 'text-yellow-600 bg-yellow-100',
      resolved: 'text-green-600 bg-green-100',
      closed: 'text-gray-600 bg-gray-100'
    };
    return colors?.[status] || colors?.new;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/admin-authentication-portal')} className="w-full">
            Return to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Operations</h1>
                <p className="text-sm text-gray-500">Recrutees Platform Management</p>
              </div>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-md mx-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search users, tickets, or actions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10 bg-gray-50 border-gray-300"
                />
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" className="relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
              </Button>

              <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{adminProfile?.user?.fullName}</p>
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${getRoleBadgeColor(adminProfile?.role)}`}>
                    {adminProfile?.role?.replace('_', ' ')?.toUpperCase()}
                  </span>
                </div>
                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-73px)] sticky top-[73px]">
          <nav className="p-4 space-y-1">
            <NavItem
              icon={LayoutDashboard}
              label="Overview"
              active={activeView === 'overview'}
              onClick={() => setActiveView('overview')}
            />
            <NavItem
              icon={Ticket}
              label="Ticket Management"
              active={activeView === 'tickets'}
              onClick={() => navigate('/admin-support-management-system')}
              badge={metrics?.urgentTickets}
            />
            <NavItem
              icon={Users}
              label="User Management"
              active={activeView === 'users'}
              onClick={() => navigate('/admin-user-account-management')}
            />
            <NavItem
              icon={Shield}
              label="Content Moderation"
              active={activeView === 'moderation'}
              onClick={() => navigate('/admin-content-moderation-dashboard')}
            />
            <NavItem
              icon={TrendingUp}
              label="Platform Analytics"
              active={activeView === 'analytics'}
              onClick={() => setActiveView('analytics')}
            />
            <NavItem
              icon={Activity}
              label="System Monitoring"
              active={activeView === 'monitoring'}
              onClick={() => setActiveView('monitoring')}
            />
            <NavItem
              icon={Settings}
              label="Settings"
              active={activeView === 'settings'}
              onClick={() => setActiveView('settings')}
            />
          </nav>
        </aside>

        {/* Main Dashboard Content */}
        <main className="flex-1 p-6">
          {activeView === 'overview' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <MetricCard
                  icon={Users}
                  label="Total Users"
                  value={metrics?.totalUsers || 0}
                  subtext={`${metrics?.activeUsers || 0} active`}
                  color="blue"
                />
                <MetricCard
                  icon={Ticket}
                  label="Open Tickets"
                  value={metrics?.openTickets || 0}
                  subtext={`${metrics?.urgentTickets || 0} urgent`}
                  color="orange"
                />
                <MetricCard
                  icon={TrendingUp}
                  label="Active Jobs"
                  value={metrics?.activeJobs || 0}
                  subtext={`${metrics?.totalCompanies || 0} companies`}
                  color="green"
                />
                <MetricCard
                  icon={Activity}
                  label="System Health"
                  value={`${((metrics?.systemHealthScore || 1) * 100)?.toFixed(0)}%`}
                  subtext="All systems operational"
                  color="purple"
                />
              </div>

              {/* Quick Actions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {filteredQuickActions?.map((action, index) => {
                  const ActionIcon = action.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(action?.path)}
                    >
                      <div className={`flex-shrink-0 mt-0.5 p-1 rounded ${getTicketStatusColor('assigned')}`}>
                        <ActionIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{action?.title}</p>
                        <p className="text-xs text-gray-500">{action?.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recent Tickets & Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Tickets */}
                <Card>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Recent Tickets</h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin-support-management-system')}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        View All
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {recentTickets?.length > 0 ? (
                        recentTickets?.map(ticket => {
                          const StatusIcon = getTicketStatusIcon(ticket?.status);
                          return (
                            <div
                              key={ticket?.id}
                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => navigate('/admin-support-management-system')}
                            >
                              <div className={`flex-shrink-0 mt-0.5 p-1 rounded ${getTicketStatusColor(ticket?.status)}`}>
                                <StatusIcon className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{ticket?.subject}</p>
                                <p className="text-xs text-gray-500">{ticket?.user?.email}</p>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${getTicketStatusColor(ticket?.status)}`}>
                                {ticket?.status}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-8">No recent tickets</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <div className="p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
                    <div className="space-y-3">
                      {recentActivity?.length > 0 ? (
                        recentActivity?.map(activity => (
                          <div key={activity?.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <Activity className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900">{activity?.actionDescription}</p>
                              <p className="text-xs text-gray-500">
                                {activity?.admin?.fullName} • {new Date(activity?.createdAt)?.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-8">No recent activity</p>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            </motion.div>
          )}

          {/* Placeholder for other views */}
          {activeView !== 'overview' && (
            <Card className="p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {activeView?.charAt(0)?.toUpperCase() + activeView?.slice(1)} View
                </h3>
                <p className="text-gray-600 mb-6">
                  This section is under development. Use the navigation to access available features.
                </p>
                <Button onClick={() => setActiveView('overview')} variant="outline">
                  Return to Overview
                </Button>
              </div>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}

// Navigation Item Component
function NavItem({ icon: Icon, label, active, onClick, badge }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-blue-50 text-blue-700'
          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <span>{label}</span>
      </div>
      {badge > 0 && (
        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
}

// Metric Card Component
function MetricCard({ icon: Icon, label, value, subtext, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    orange: 'bg-orange-100 text-orange-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses?.[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
        <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
        <p className="text-xs text-gray-500">{subtext}</p>
      </div>
    </Card>
  );
}